import { GripVertical, Link2, Plus, Trash2 } from 'lucide-react';
import React, { useRef, useState } from 'react';

import type { StepData, StepItem } from '../../../../types/blog';

interface Props {
  data: StepData;
  onChange: (data: StepData) => void;
}

const inputCls = 'ds-input ds-input-sm';

// Mini link-insert popover shared by all textarea rows
interface LinkPopoverProps {
  onInsert: (url: string, anchor: string) => void;
  onClose: () => void;
}
const LinkPopover: React.FC<LinkPopoverProps> = ({ onInsert, onClose }) => {
  const [url, setUrl] = useState('https://');
  const [anchor, setAnchor] = useState('');
  return (
    <div className="ds-card-pop absolute right-0 top-full mt-1 z-30 w-64 flex flex-col gap-2">
      <p className="t-card-title">Insert link</p>
      <input
        autoFocus
        value={url}
        onChange={(e) => setUrl(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Escape') onClose(); if (e.key === 'Enter') { e.preventDefault(); if (url.trim() && url !== 'https://') onInsert(url.trim(), anchor.trim()); } }}
        placeholder="https://example.com"
        className="ds-input ds-input-sm"
      />
      <input
        value={anchor}
        onChange={(e) => setAnchor(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Escape') onClose(); if (e.key === 'Enter') { e.preventDefault(); if (url.trim() && url !== 'https://') onInsert(url.trim(), anchor.trim()); } }}
        placeholder="Anchor text (optional)"
        className="ds-input ds-input-sm"
      />
      <div className="flex gap-2">
        <button type="button" onClick={() => { if (url.trim() && url !== 'https://') onInsert(url.trim(), anchor.trim()); }}
          disabled={!url.trim() || url === 'https://'}
          className="ds-btn ds-btn-primary ds-btn-sm flex-1 disabled:opacity-40">Insert</button>
        <button type="button" onClick={onClose}
          className="ds-btn ds-btn-secondary ds-btn-sm flex-1">Cancel</button>
      </div>
    </div>
  );
};

// A textarea with an inline link button
interface LinkedTextareaProps {
  value: string;
  onChange: (v: string) => void;
  rows?: number;
  placeholder?: string;
}
const LinkedTextarea: React.FC<LinkedTextareaProps> = ({ value, onChange, rows = 2, placeholder }) => {
  const ref = useRef<HTMLTextAreaElement>(null);
  const [showLink, setShowLink] = useState(false);

  const handleInsert = (url: string, anchor: string) => {
    const el = ref.current;
    if (!el) return;
    const { selectionStart: ss, selectionEnd: se } = el;
    const text = anchor || value.slice(ss, se) || 'link';
    const md = `[${text}](${url})`;
    onChange(value.slice(0, ss) + md + value.slice(se));
    setShowLink(false);
    requestAnimationFrame(() => { el.focus(); el.setSelectionRange(ss, ss + md.length); });
  };

  return (
    <div className="relative flex-1">
      <div className="relative">
        <textarea
          ref={ref}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          rows={rows}
          className={`${inputCls} resize-none pr-8`}
        />
        <button
          type="button"
          title="Insert link"
          onClick={() => setShowLink((v) => !v)}
          className="ds-btn ds-btn-ghost ds-btn-sm absolute right-1.5 top-1.5 w-6 h-6 p-0"
          style={showLink ? { color: 'var(--ds-brand)' } : undefined}
        >
          <Link2 className="w-3.5 h-3.5" />
        </button>
      </div>
      {showLink && (
        <LinkPopover onInsert={handleInsert} onClose={() => setShowLink(false)} />
      )}
    </div>
  );
};

export const StepSectionEditor: React.FC<Props> = ({ data, onChange }) => {
  const update = (idx: number, patch: Partial<StepItem>) => {
    const steps = data.steps.map((s, i) => (i === idx ? { ...s, ...patch } : s));
    onChange({ steps });
  };

  const add = () =>
    onChange({ steps: [...data.steps, { title: '', description: '' }] });

  const remove = (idx: number) =>
    onChange({ steps: data.steps.filter((_, i) => i !== idx) });

  return (
    <div className="flex flex-col gap-3">
      {data.steps.map((step, idx) => (
        <div key={idx} className="flex items-start gap-2">
          <GripVertical className="w-4 h-4 mt-2.5 shrink-0" style={{ color: 'var(--ds-text-muted)' }} />
          <span
            className="w-6 h-6 mt-2 rounded-full t-tag flex items-center justify-center shrink-0"
            style={{ background: 'var(--ds-brand)', color: 'var(--ds-text-on-brand)' }}
          >
            {idx + 1}
          </span>
          <div className="flex-1 flex flex-col gap-1.5">
            <input
              value={step.title}
              onChange={(e) => update(idx, { title: e.target.value })}
              placeholder={`Step ${idx + 1} title`}
              className={inputCls}
            />
            <LinkedTextarea
              value={step.description ?? ''}
              onChange={(v) => update(idx, { description: v })}
              placeholder="Optional description — click 🔗 to insert a link"
            />
          </div>
          <button
            type="button"
            onClick={() => remove(idx)}
            className="mt-2 ds-btn ds-btn-ghost ds-btn-sm p-0 w-7 hover:text-[var(--ds-danger)]"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      ))}
      <button
        type="button"
        onClick={add}
        className="ds-btn ds-btn-secondary ds-btn-sm w-fit"
      >
        <Plus className="w-4 h-4" /> Add step
      </button>
    </div>
  );
};
